import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Spinner,
  VStack,
  Text,
} from "@chakra-ui/react";
import { ReactElement, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { format, startOfYear, endOfYear } from "date-fns";
import { it } from "date-fns/locale";
import { capitalize } from "lodash";
import { LuPrinter } from "react-icons/lu";
import { GenericTable, TableColumn } from "../ui/table";
import useFetchBookingSummary from "./hooks/useFetchBookingSummary";
import useShowToast from "../../hooks/useShowToast";
import api from "../../api";
import { formatCurrency } from "../../utils/numbers";

type BookingSummaryTableContent = {
  month: string;
  totalBookings: number;
  totalPaidBookings: number;
  totalReceipts: number;
  totalAmount: number;
  previousYearAmount: number;
};

const ClubManagementBookings = (): ReactElement => {
  const { t } = useTranslation();
  const [dateFrom, setDateFrom] = useState<Date>(startOfYear(new Date()));
  const [dateTo, setDateTo] = useState<Date>(endOfYear(new Date()));
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const { bookingSummary, loading, refetchSummary } = useFetchBookingSummary();
  const { errorToast } = useShowToast();

  useEffect(() => {
    refetchSummary(dateFrom, dateTo);
  }, []);

  const resetFilters = () => {
    const now = new Date();
    setDateFrom(startOfYear(now));
    setDateTo(endOfYear(now));
    refetchSummary(dateFrom, dateTo);
  };

  const refetch = () => {
    refetchSummary(dateFrom, dateTo);
  };

  const handlePrintSummary = async () => {
    setIsPrintLoading(true);
    await api.printBookingSummary(dateFrom, dateTo);
    if (api.error) {
      errorToast({
        title: t("club_management.bookings.print.error_title"),
        description: t("club_management.bookings.print.error_description"),
      });
    }
    setIsPrintLoading(false);
  };

  // Transform API data to table format
  const bookingSummaryTableContent = useMemo<BookingSummaryTableContent[]>(() => {
    const monthlyData = bookingSummary.map(bookingSummaryMonth => {
      const monthDate = new Date(bookingSummaryMonth.year, bookingSummaryMonth.month - 1, 1);
      const monthLabel = capitalize(format(monthDate, "MMMM yyyy", { locale: it }));

      return {
        month: monthLabel,
        totalBookings: bookingSummaryMonth.total_bookings,
        totalPaidBookings: bookingSummaryMonth.total_paid_bookings,
        totalReceipts: bookingSummaryMonth.total_receipts,
        totalAmount: bookingSummaryMonth.total_amount,
        previousYearAmount: bookingSummaryMonth.total_amount_previous_year,
      };
    });

    const totals = {
      totalBookings: 0,
      totalPaidBookings: 0,
      totalReceipts: 0,
      totalAmount: 0,
      previousYearAmount: 0,
    };

    monthlyData.forEach(row => {
      totals.totalBookings += row.totalBookings;
      totals.totalPaidBookings += row.totalPaidBookings;
      totals.totalReceipts += row.totalReceipts;
      totals.totalAmount += row.totalAmount;
      totals.previousYearAmount += row.previousYearAmount;
    });

    return [
      ...monthlyData,
      { month: t("club_management.bookings.totals.label"), ...totals, isTotal: true },
    ];
  }, [bookingSummary, t]);

  const columns: TableColumn[] = useMemo(
    () => [
      {
        label: t("club_management.bookings.columns.month"),
        key: "month",
        sortable: true,
        render: (value: unknown, row: Record<string, unknown>) => (
          <Text fontWeight={row.isTotal ? "bold" : "normal"}>{value as string}</Text>
        ),
      },
      {
        label: t("club_management.bookings.columns.total_bookings"),
        key: "totalBookings",
        sortable: true,
        render: (value: unknown, row: Record<string, unknown>) => (
          <Text fontWeight={row.isTotal ? "bold" : "normal"}>{value as number}</Text>
        ),
      },
      {
        label: t("club_management.bookings.columns.total_paid_bookings"),
        key: "totalPaidBookings",
        sortable: true,
        render: (value: unknown, row: Record<string, unknown>) => (
          <Text fontWeight={row.isTotal ? "bold" : "normal"}>{value as number}</Text>
        ),
      },
      {
        label: t("club_management.bookings.columns.total_receipts"),
        key: "totalReceipts",
        sortable: true,
        render: (value: unknown, row: Record<string, unknown>) => (
          <Text fontWeight={row.isTotal ? "bold" : "normal"}>{value as number}</Text>
        ),
      },
      {
        label: t("club_management.bookings.columns.total_amount"),
        key: "totalAmount",
        sortable: true,
        render: (value: unknown, row: Record<string, unknown>) => (
          <Text fontWeight={row.isTotal ? "bold" : "normal"}>
            {formatCurrency(value as number)}
          </Text>
        ),
      },
      {
        label: t("club_management.bookings.columns.previous_year_amount"),
        key: "previousYearAmount",
        sortable: true,
        render: (value: unknown, row: Record<string, unknown>) => (
          <Text fontWeight={row.isTotal ? "bold" : "normal"}>
            {formatCurrency(value as number)}
          </Text>
        ),
      },
    ],
    [t],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="200px">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ base: "stretch", md: "center" }}
        gap={3}
        width="100%"
      >
        <Heading size="lg">{t("club_management.bookings.title")}</Heading>
        <Button
          leftIcon={<LuPrinter />}
          colorScheme="blue"
          size="sm"
          onClick={handlePrintSummary}
          isLoading={isPrintLoading}
        >
          {t("club_management.bookings.actions.print")}
        </Button>
      </Box>

      {/* Date Filters */}
      <Box p={4} bg="gray.50" borderRadius="md">
        <Box
          display="flex"
          flexDirection={{ base: "column", md: "row" }}
          gap={4}
          alignItems={{ base: "stretch", md: "end" }}
        >
          <FormControl>
            <FormLabel>{t("club_management.bookings.filters.date_from")}</FormLabel>
            <Input
              type="date"
              value={format(dateFrom, "yyyy-MM-dd")}
              onChange={e => setDateFrom(new Date(e.target.value))}
              size="sm"
            />
          </FormControl>
          <FormControl>
            <FormLabel>{t("club_management.bookings.filters.date_to")}</FormLabel>
            <Input
              type="date"
              value={format(dateTo, "yyyy-MM-dd")}
              onChange={e => setDateTo(new Date(e.target.value))}
              size="sm"
            />
          </FormControl>
          <Button onClick={refetch} size="sm" pl={8} pr={8} colorScheme="green">
            {t("club_management.bookings.filters.filter")}
          </Button>
          <Button onClick={resetFilters} size="sm" pl={8} pr={8} colorScheme="blue">
            {t("club_management.bookings.filters.reset")}
          </Button>
        </Box>
      </Box>

      <GenericTable
        data={bookingSummaryTableContent as unknown as Record<string, unknown>[]}
        columns={columns}
        size="sm"
      />
    </VStack>
  );
};

export default ClubManagementBookings;
